// Beside the Debug: Graph drawing: what the graph finds, or the selected item. A check opens
// to its actions as the Next actions table's own rows, as the Ready to build card opens it.
// See docs/Y2026-Q4/design-debug-graph.md.
import type { ReactNode } from "react";

import type { ProjectAgentStatusList } from "../api/hooks/useProjectAgentStatus";
import { formatDay } from "../utils/attributeFormat";
import {
  type Finding,
  type Graph,
  type GraphLink,
  conditionLabel,
} from "../utils/dependencyGraph";
import {
  CHECK_STATE_LABEL,
  type CheckState,
  checkState,
  hardnessLabel,
  itemFacts,
  NO_ACTION_NO_REASON,
  optionLines,
  projectSource,
  resolvingActions,
  waitCondition,
  workedCheck,
  workedChecks,
  worksTowards,
} from "../utils/nextActions";
import { ActionTable } from "./NextActionTable";
import { CheckSource, ForActionChip, WaitingFor } from "./ReadyToBuild";
import { reviewAttrs } from "../utils/reviewState";
import styles from "./DependencyGraphDetail.module.css";

type Cite = (text: string) => ReactNode;

const STATE_TONE: Record<CheckState, string> = {
  met: styles.chipGood,
  worked: "",
  waiting: "",
  wait_ended: styles.chipBad,
  nobody: styles.chipBad,
};

/** An item on the project as a button that selects it on the graph, with its facts. */
const ItemButton = ({
  id,
  graph,
  data,
  agentLabel,
  onSelect,
  children,
}: {
  id: string;
  graph: Graph;
  data: ProjectAgentStatusList;
  agentLabel: (agent: string) => string;
  onSelect: (id: string) => void;
  children?: ReactNode;
}) => {
  const facts = itemFacts(id, graph.items, data.team);
  if (!facts) return <li className={styles.muted}>An item no longer on this project</li>;
  const when = facts.when === null ? null : facts.when ? formatDay(facts.when) : "no date";
  return (
    <li>
      <button type="button" className={styles.link} onClick={() => onSelect(id)}>
        {facts.title}
      </button>
      <span className={styles.meta}>
        {[agentLabel(facts.agent), when, facts.owner, facts.state].filter(Boolean).join(" · ")}
      </span>
      {children}
    </li>
  );
};

const Relations = ({
  title,
  links,
  end,
  graph,
  data,
  agentLabel,
  onSelect,
  because,
}: {
  title: string;
  links: GraphLink[];
  end: "from" | "to";
  graph: Graph;
  data: ProjectAgentStatusList;
  agentLabel: (agent: string) => string;
  onSelect: (id: string) => void;
  because: (link: GraphLink) => ReactNode;
}) =>
  links.length === 0 ? null : (
    <section>
      <h4 className={styles.head}>{title}</h4>
      <ul className={styles.list}>
        {links.map((l) => (
          <ItemButton key={`${l.from}|${l.to}`} id={l[end]} graph={graph} data={data} agentLabel={agentLabel} onSelect={onSelect}>
            <span className={styles.meta}>{because(l)}</span>
          </ItemButton>
        ))}
      </ul>
    </section>
  );

const TONE_LABEL: Record<Finding["tone"], [string, string]> = {
  red: ["problem", "problems"],
  amber: ["warning", "warnings"],
  info: ["note", "notes"],
};

/** The findings counted by tone, for the bar above the graph; opens the full list. */
export const FindingsSummary = ({
  findings,
  onOpen,
}: {
  findings: Finding[];
  onOpen: () => void;
}) => (
  <button type="button" className={styles.summary} onClick={onOpen}>
    What the graph finds:
    {(["red", "amber", "info"] as const).map((tone) => {
      const n = findings.filter((f) => f.tone === tone).length;
      return n === 0 ? null : (
        <span key={tone} className={styles.summaryCount}>
          <span className={`${styles.sev} ${styles[tone]}`} aria-hidden="true" />
          {n} {TONE_LABEL[tone][n === 1 ? 0 : 1]}
        </span>
      );
    })}
  </button>
);

export const FindingsList = ({
  findings,
  mode,
  onSelect,
}: {
  findings: Finding[];
  mode: Graph["mode"];
  onSelect: (id: string) => void;
}) => (
  <div className={styles.pane} {...reviewAttrs("specialist_details", "Dependency graph findings")}>
    <h3 className={styles.title}>What the graph finds</h3>
    <ul className={styles.findings}>
      {findings.map((f, i) => (
        <li key={i}>
          <span className={`${styles.sev} ${styles[f.tone]}`} aria-hidden="true" />
          <span>
            {f.id ? (
              <button type="button" className={styles.link} onClick={() => onSelect(f.id as string)}>
                {f.lead}
              </button>
            ) : (
              <strong>{f.lead}</strong>
            )}{" "}
            {f.text}
          </span>
        </li>
      ))}
    </ul>
    <h4 className={styles.head}>How to read it</h4>
    <p className={styles.muted}>
      {mode === "checks"
        ? "Each card is a Ready to build item, on the date its last open action is due or, with none, the date of what it waits for. An arrow means the second item can't be met until the first is: an action on it waits on an action on the first, or the item records that it waits for it. +A marks an item added only so an action had one."
        : "Each card is a next action, on the date its row shows. A waiting action with no date sits just after what it waits on."}{" "}
      Dark arrows cross between agents; red ones run backwards in time. Select a card to trace
      what it needs and what it holds up.
    </p>
  </div>
);

export const DependencyGraphDetail = ({
  id,
  graph,
  data,
  projectId,
  agentLabel,
  cite,
  preview,
  onPreview,
  onSelect,
  onShowActions,
}: {
  id: string;
  graph: Graph;
  data: ProjectAgentStatusList;
  projectId: string;
  agentLabel: (agent: string) => string;
  cite: Cite;
  preview: number | null;
  onPreview: (option: number | null) => void;
  onSelect: (id: string | null) => void;
  onShowActions: () => void;
}) => {
  const { items } = graph;
  const into = graph.links.filter((l) => l.to === id && l.kind === "waits" && !l.satisfied);
  const outOf = graph.links.filter((l) => l.from === id && l.kind === "waits" && !l.satisfied);
  const serves = graph.links.filter((l) => l.from === id && l.kind === "serves");
  const relationProps = { graph, data, agentLabel, onSelect };
  // A comment selected here goes to the specialist whose item it is.
  const paneReview = (agent: string, title: string, itemId: string) =>
    reviewAttrs("specialist_details", `${agentLabel(agent)} > ${title}`.slice(0, 200), {
      agent,
      itemId,
    });
  const back = (
    <button type="button" className={styles.back} onClick={() => onSelect(null)}>
      ← What the graph finds
    </button>
  );
  const describeBecause = (l: GraphLink) =>
    l.because.length
      ? `Because ${l.because
          .map((b) =>
            b.direct
              ? `it records that it waits for "${itemFacts(b.from, items, data.team)?.title ?? b.from}"`
              : `"${items.actions.get(b.to)?.action.title ?? b.to}" waits for "${itemFacts(b.from, items, data.team)?.title ?? b.from}"${conditionLabel(b.needs) ? ` (${conditionLabel(b.needs)})` : ""}`,
          )
          .join("; ")}`
      : l.needs
        ? waitCondition(l.needs, items.actions.get(l.from) ? data.team[items.actions.get(l.from)?.action.internal_owner ?? ""] ?? "" : "")
        : "";

  const check = items.checks.get(id);
  if (check) {
    const state = checkState(check.check, items);
    const working = resolvingActions(items, id);
    return (
      <div className={styles.pane} {...paneReview(check.agent, check.check.text, id)}>
        {back}
        <h3 className={styles.title}>{check.check.text}</h3>
        <div className={styles.chips}>
          <span className={styles.chip}>{agentLabel(check.agent)}</span>
          <span className={`${styles.chip} ${STATE_TONE[state]}`}>{CHECK_STATE_LABEL[state]}</span>
          <ForActionChip check={check.check} />
        </div>
        <p className={styles.muted}>
          <CheckSource check={check.check} cite={cite} projectId={projectId} />
        </p>
        {check.check.met && (
          <p className={styles.muted}>
            Met {formatDay(check.check.met_on)} {cite(check.check.evidence)}
          </p>
        )}
        {working.length > 0 ? (
          <section>
            <h4 className={styles.head}>Worked on by</h4>
            <div className={styles.table}>
              <ActionTable
                scope={{ kind: "agent", agent: check.agent }}
                sources={[projectSource(projectId, data)]}
                include={(action) => worksTowards(action, id)}
                empty=""
                controls={false}
                showArea={false}
                anchored={false}
              />
            </div>
            {graph.mode === "checks" && (
              <button type="button" className={styles.button} onClick={onShowActions}>
                Show its actions on the graph
              </button>
            )}
          </section>
        ) : check.check.no_action_reason ? (
          <WaitingFor check={check.check} items={items} team={data.team} projectId={projectId} />
        ) : !check.check.met ? (
          <p className={styles.bad}>{NO_ACTION_NO_REASON}</p>
        ) : null}
        {graph.mode === "checks" && (
          <>
            <Relations title="Needs first" links={into} end="from" because={describeBecause} {...relationProps} />
            <Relations title="Holding up" links={outOf} end="to" because={describeBecause} {...relationProps} />
            <Relations title="Works towards" links={serves} end="to" because={() => ""} {...relationProps} />
          </>
        )}
      </div>
    );
  }

  const action = items.actions.get(id);
  if (action) {
    const options = [...optionLines(action.action.call_to_action).keys()];
    const conditional = outOf.some((l) => l.needs?.kind === "options");
    // A closed action keeps every item it worked towards, the ones its ask also asked for
    // included; only an open one can lack one.
    const worked = action.action.closed
      ? workedChecks(action.action).flatMap((c) => {
          const check = items.checks.get(c)?.check;
          return check ? [check] : [];
        })
      : [workedCheck(items, action.action)].filter((c) => c !== undefined);
    return (
      <div className={styles.pane} {...paneReview(action.agent, action.action.title, id)}>
        {back}
        <div className={styles.table}>
          <ActionTable
            scope={{ kind: "agent", agent: action.agent }}
            sources={[projectSource(projectId, data)]}
            include={(a) => a.action_id === id}
            empty=""
            controls={false}
            showArea={false}
            anchored={false}
          />
        </div>
        <section>
          <h4 className={styles.head}>Works towards</h4>
          {worked.length ? (
            <ul className={styles.list}>
              {worked.map((c) => (
                <ItemButton key={c.check_id} id={c.check_id} {...relationProps} />
              ))}
            </ul>
          ) : (
            <p className={styles.bad}>No open checklist item. The pass gate should send the agent back.</p>
          )}
        </section>
        {action.action.type === "decision" && conditional && !action.action.closed && (
          <section>
            <h4 className={styles.head}>Preview an answer</h4>
            <p className={styles.muted}>Which waiting actions would go ahead, and which would need a look.</p>
            <div className={styles.options} role="group" aria-label="Preview an answer">
              {options.map((n) => (
                <button key={n} type="button" className={styles.option} aria-pressed={preview === n} onClick={() => onPreview(preview === n ? null : n)}>
                  Option {n}
                </button>
              ))}
            </div>
          </section>
        )}
        <Relations title="Waits for" links={into} end="from" because={describeBecause} {...relationProps} />
        <Relations
          title="Holding up"
          links={outOf}
          end="to"
          because={(l) => {
            const verdict =
              preview === null
                ? ""
                : l.needs?.kind === "options" && !l.needs.options.includes(preview)
                  ? "Would need a look. "
                  : "Would go ahead. ";
            return `${verdict}${l.needs ? waitCondition(l.needs, data.team[action.action.internal_owner] ?? "") : ""}`;
          }}
          {...relationProps}
        />
        <Relations title="Works towards a date" links={serves} end="to" because={() => ""} {...relationProps} />
      </div>
    );
  }

  const date = items.dates.get(id);
  if (!date) return back;
  const servedBy = graph.links.filter((l) => l.to === id && l.kind === "serves");
  return (
    <div className={styles.pane} {...paneReview(date.agent, date.date.what, id)}>
      {back}
      <h3 className={styles.title}>{date.date.what}</h3>
      <div className={styles.chips}>
        <span className={styles.chip}>{agentLabel(date.agent)}</span>
        <span className={`${styles.chip} ${date.date.hardness === "hard" ? styles.chipBad : ""}`}>
          {hardnessLabel(date.date.hardness)}
        </span>
        {date.date.closed && <span className={styles.chip}>{date.date.closed.outcome}</span>}
      </div>
      <p className={styles.muted}>
        {date.date.date ? formatDay(date.date.date) : "No date set"}
        {date.date.consequence ? `. If missed: ${date.date.consequence}` : ""}
        {date.date.no_action_reason ? `. ${date.date.no_action_reason}` : ""}
      </p>
      <Relations title="Worked towards by" links={servedBy} end="from" because={() => ""} {...relationProps} />
      <Relations title="Unlocks" links={outOf} end="to" because={describeBecause} {...relationProps} />
    </div>
  );
};
